import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import React from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router';

import type { Creative } from '@/api/types/creative';
import { Menu } from '@/common/menu';
import { BASE_Z_INDEX } from '@/common/z-index';

import { CreativeThumbnail } from './creative-thumbnail';

interface CreativeTableProps {
    creatives: Creative[];
    className?: string;
    onDuplicate: (id: number) => void;
    onDelete: (id: number) => void;
}

export const CreativeTable: FC<CreativeTableProps> = ({ creatives, className, onDuplicate, onDelete }) => {
    const navigate = useNavigate();

    return (
        <div className={`w-full flex flex-col flex-grow overflow-hidden ${className}`}>
            <div className="overflow-y-auto flex-grow no-scrollbar">
                <table className="w-full table-fixed">
                    <thead
                        className="sticky top-0 bg-gray-150 p-3 text-left rounded-md"
                        style={{ zIndex: BASE_Z_INDEX }}
                    >
                        <tr>
                            <th className="w-32 rounded-l-md" />
                            <th className="py-3 text-white text-sm font-normal">Title</th>
                            <th className="py-3 text-white text-sm font-normal">Template</th>
                            <th className="py-3 text-white text-sm font-normal">Assets</th>
                            <th className="py-3 w-36 text-white text-sm font-normal rounded-r-md">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {creatives.map((creative) => (
                            <tr key={creative.id}>
                                <td className="flex flex-row justify-start w-32 mr-3 my-3">
                                    <CreativeThumbnail thumbnail={creative.templateVersion.thumbnail} />
                                </td>
                                <td className="py-3 text-white text-sm">{creative.name}</td>
                                <td className="py-3 text-white text-sm">{creative.templateName}</td>
                                <td
                                    className={`py-3 text-sm ${creative.assetsCount === 0 ? 'text-red-50' : 'text-white'}`}
                                >
                                    {creative.assetsCount === 0 ? 'Draft' : creative.assetsCount}
                                </td>
                                <td className="py-3 w-36">
                                    <div className="flex flex-row gap-2">
                                        <button
                                            type="button"
                                            className="border-gray-100 border-solid border-[1px] text-white text-sm h-8 px-4 rounded-md hover:bg-gray-160"
                                            onClick={() => {
                                                if (creative.assetsCount > 0) {
                                                    navigate(`/creatives/assets/${creative.id}`);
                                                } else {
                                                    navigate(`/creatives/generate/${creative.id}`);
                                                }
                                            }}
                                        >
                                            View
                                        </button>
                                        <Menu
                                            icon={
                                                <div className="flex flex-row items-center border-gray-100 border-solid border-[1px] text-white text-sm h-8 px-2 rounded-md hover:bg-gray-160">
                                                    <EllipsisVerticalIcon className="text-white h-5 w-5" />
                                                </div>
                                            }
                                            items={[
                                                {
                                                    id: 'crtv-menu-edit',
                                                    name: 'Export Creative',
                                                    onClick: () => {
                                                        if (creative.assetsCount > 0) {
                                                            navigate(`/creatives/assets/${creative.id}`);
                                                        } else {
                                                            navigate(`/creatives/generate/${creative.id}`);
                                                        }
                                                    },
                                                },
                                                {
                                                    id: 'crtv-menu-duplicate',
                                                    name: 'Duplicate Creative',
                                                    onClick: () => onDuplicate(creative.id),
                                                },
                                                {
                                                    id: 'crtv-menu-delete',
                                                    name: 'Delete Creative',
                                                    onClick: () => onDelete(creative.id),
                                                    color: 'red-50',
                                                },
                                            ]}
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
